<template>
  <div class="min-h-screen relative overflow-hidden pb-[100px]">
    <!-- Background decorative elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/30 to-blue-300/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-indigo-200/30 to-purple-300/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/2 left-1/4 w-32 h-32 bg-gradient-to-br from-cyan-200/20 to-blue-300/10 rounded-full blur-2xl"
      ></div>
    </div>

    <div class="relative pt-16 sm:pt-20 lg:pt-24 pb-12 px-3 sm:px-4">
      <div v-if="!isLoading" class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          <!-- Aperçu du compte - Premier sur mobile, à droite sur desktop -->
          <div class="xl:col-span-1 xl:order-2">
            <div class="xl:sticky xl:top-8">
              <div
                class="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl shadow-gray-200/50 p-4 sm:p-6 mb-4 sm:mb-6"
              >
                <div class="flex items-center mb-4 sm:mb-6">
                  <div
                    class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-3"
                  >
                    <i class="fa-solid fa-info-circle text-white text-sm sm:text-base"></i>
                  </div>
                  <h3 class="text-base sm:text-lg font-bold text-gray-900">Aperçu du compte</h3>
                </div>

                <div v-if="authStore.user" class="space-y-3 sm:space-y-4">
                  <div
                    class="rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200/50"
                    :class="
                      authStore.user?.current_subscription?.package?.name
                        ? 'border-primary-200 border-2 bg-primary-50'
                        : ''
                    "
                  >
                    <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Package
                    </p>
                    <p
                      class="text-gray-900 truncate text-primary-500 font-bold text-sm sm:text-base"
                      v-if="authStore.user?.current_subscription?.package?.name"
                    >
                      {{ authStore.user?.current_subscription?.package?.name }}
                    </p>
                    <p v-else class="text-sm sm:text-base">Sans package</p>
                    <span
                      v-if="authStore.user?.current_subscription?.package?.name"
                      class="text-xs text-gray-400"
                      >Jusqu'au
                      {{ formatDate(authStore.user?.current_subscription?.end_date, false) }}</span
                    >
                  </div>

                  <div class="rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200/50">
                    <p class="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      Nom complet
                    </p>
                    <p class="text-gray-900 font-medium text-sm sm:text-base">
                      {{
                        (authStore.user.first_name || '') +
                          ' ' +
                          (authStore.user.last_name || '') || 'Non défini'
                      }}
                    </p>
                  </div>

                  <div class="rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200/50">
                    <p class="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      Email
                    </p>
                    <p class="text-gray-900 font-medium truncate text-sm sm:text-base">
                      {{ authStore.user.email || 'Non défini' }}
                    </p>
                  </div>

                  <div class="rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200/50">
                    <p class="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      Téléphone
                    </p>
                    <p class="text-gray-900 font-medium text-sm sm:text-base">
                      {{ authStore.user.phone || 'Non défini' }}
                    </p>
                  </div>

                  <div
                    v-if="authStore.user.last_login"
                    class="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-amber-200/50"
                  >
                    <p class="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">
                      Dernière connexion
                    </p>
                    <p class="text-amber-900 font-medium text-sm sm:text-base">
                      {{ formatDate(authStore.user.last_login) }}
                    </p>
                  </div>
                </div>

                <div v-else class="text-center py-8 sm:py-12">
                  <div
                    class="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                  >
                    <i class="fa-solid fa-user-slash text-gray-400 text-lg sm:text-2xl"></i>
                  </div>
                  <p class="text-gray-500 font-medium text-sm sm:text-base">
                    Aucune information disponible
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Formulaire principal - Deuxième sur mobile, à gauche sur desktop -->
          <div class="xl:col-span-3 xl:order-1">
            <div
              class="bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg sm:shadow-xl shadow-gray-200/50 p-4 sm:p-6 lg:p-8"
            >
              <div class="space-y-6 sm:space-y-8">
                <div class="space-y-4 sm:space-y-6">
                  <!-- Prénom et Nom -->
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div class="group">
                      <label
                        for="firstName"
                        class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                      >
                        <div class="flex items-center">
                          <i class="fa-solid fa-signature text-primary-500 mr-2 text-sm"></i>
                          Prénom
                        </div>
                      </label>
                      <div class="relative">
                        <input
                          id="firstName"
                          v-model="form.first_name"
                          type="text"
                          class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                          placeholder="Votre prénom"
                        />
                        <div
                          class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                        >
                          <i class="fa-solid fa-user-tag text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>
                    <div class="group">
                      <label
                        for="lastName"
                        class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                      >
                        <div class="flex items-center">
                          <i class="fa-solid fa-signature text-primary-500 mr-2 text-sm"></i>
                          Nom
                        </div>
                      </label>
                      <div class="relative">
                        <input
                          id="lastName"
                          v-model="form.last_name"
                          type="text"
                          class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                          placeholder="Votre nom"
                        />
                        <div
                          class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                        >
                          <i class="fa-solid fa-user-tag text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <!-- Email -->
                    <div class="group">
                      <label
                        for="email"
                        class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                      >
                        <div class="flex items-center">
                          <i class="fa-solid fa-envelope text-primary-500 mr-2 text-sm"></i>
                          Adresse email
                        </div>
                      </label>
                      <div class="relative">
                        <input
                          id="email"
                          v-model="form.email"
                          type="email"
                          class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                          placeholder="votre.email@exemple.com"
                        />
                        <div
                          class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                        >
                          <i class="fa-solid fa-at text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>

                    <!-- Téléphone -->
                    <div class="group">
                      <label
                        for="phone"
                        class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                      >
                        <div class="flex items-center">
                          <i class="fa-solid fa-phone text-primary-500 mr-2 text-sm"></i>
                          Numéro de téléphone
                        </div>
                      </label>
                      <div class="relative">
                        <input
                          id="phone"
                          v-model="form.phone"
                          type="tel"
                          class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                          placeholder="+237 6 XX XX XX XX"
                        />
                        <div
                          class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                        >
                          <i class="fa-solid fa-mobile-alt text-gray-400 text-sm"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Section changement de mot de passe -->
                <div
                  class="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-200/50"
                >
                  <div
                    class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0"
                  >
                    <div class="flex items-center">
                      <div
                        class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center mr-3 sm:mr-4"
                      >
                        <i class="fa-solid fa-shield-halved text-white text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 class="text-lg sm:text-xl font-bold text-gray-900">Sécurité</h3>
                        <p class="text-gray-600 text-sm sm:text-base">Changez votre mot de passe</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      @click="showPasswordForm = !showPasswordForm"
                      class="w-full sm:w-auto relative cursor-pointer overflow-hidden group bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
                    >
                      <span class="relative z-10 flex items-center justify-center">
                        <i
                          :class="showPasswordForm ? 'fa-solid fa-times' : 'fa-solid fa-edit'"
                          class="mr-2"
                        ></i>
                        {{ showPasswordForm ? 'Annuler' : 'Modifier' }}
                      </span>
                      <div
                        class="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
                      ></div>
                    </button>
                  </div>

                  <div v-if="showPasswordForm" class="space-y-4 sm:space-y-6">
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                      <div class="group">
                        <label
                          for="currentPassword"
                          class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                        >
                          <div class="flex items-center">
                            <i class="fa-solid fa-key text-amber-500 mr-2 text-sm"></i>
                            <span class="hidden sm:inline">Mot de passe actuel</span>
                            <span class="sm:hidden">Actuel</span>
                          </div>
                        </label>
                        <div class="relative">
                          <input
                            id="currentPassword"
                            v-model="passwordForm.oldPassword"
                            type="password"
                            class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                            placeholder="Mot de passe actuel"
                          />
                          <div
                            class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                          >
                            <i class="fa-solid fa-lock text-gray-400 text-sm"></i>
                          </div>
                        </div>
                      </div>
                      <div class="group">
                        <label
                          for="newPassword"
                          class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                        >
                          <div class="flex items-center">
                            <i class="fa-solid fa-key text-green-500 mr-2 text-sm"></i>
                            <span class="hidden sm:inline">Nouveau mot de passe</span>
                            <span class="sm:hidden">Nouveau</span>
                          </div>
                        </label>
                        <div class="relative">
                          <input
                            id="newPassword"
                            v-model="passwordForm.newPassword"
                            type="password"
                            class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                            placeholder="Nouveau mot de passe"
                          />
                          <div
                            class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                          >
                            <i class="fa-solid fa-lock-open text-gray-400 text-sm"></i>
                          </div>
                        </div>
                      </div>
                      <div class="group">
                        <label
                          for="confirmPassword"
                          class="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3"
                        >
                          <div class="flex items-center">
                            <i class="fa-solid fa-shield-check text-green-500 mr-2 text-sm"></i>
                            Confirmation
                          </div>
                        </label>
                        <div class="relative">
                          <input
                            id="confirmPassword"
                            v-model="passwordForm.confirmPassword"
                            type="password"
                            class="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 bg-white hover:border-gray-300 group-hover:shadow-md text-sm sm:text-base"
                            placeholder="Confirmez le mot de passe"
                          />
                          <div
                            class="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none"
                          >
                            <i class="fa-solid fa-check-double text-gray-400 text-sm"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Boutons d'action -->
                <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-8">
                  <button
                    type="button"
                    @click="handleSubmit"
                    :disabled="authStore.isLoading"
                    class="flex-1 relative cursor-pointer overflow-hidden group bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl disabled:shadow-md text-sm sm:text-base"
                  >
                    <span class="relative z-10 flex items-center justify-center">
                      <i
                        v-if="authStore.isLoading"
                        class="fa-solid fa-spinner fa-spin mr-2 sm:mr-3 text-base sm:text-lg"
                      ></i>
                      <i v-else class="fa-solid fa-save mr-2 sm:mr-3 text-base sm:text-lg"></i>
                      <span class="hidden sm:inline">{{
                        authStore.isLoading
                          ? 'Sauvegarde en cours...'
                          : 'Sauvegarder les modifications'
                      }}</span>
                      <span class="sm:hidden">{{
                        authStore.isLoading ? 'Sauvegarde...' : 'Sauvegarder'
                      }}</span>
                    </span>
                    <div
                      class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
                    ></div>
                  </button>

                  <button
                    type="button"
                    @click="resetForm"
                    class="sm:flex-none cursor-pointer relative overflow-hidden group bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg text-sm sm:text-base"
                  >
                    <span class="relative z-10 flex items-center justify-center">
                      <i class="fa-solid fa-undo mr-2 sm:mr-3"></i>
                      Annuler
                    </span>
                    <div
                      class="absolute inset-0 bg-white/50 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right duration-300"
                    ></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingState v-if="isLoading" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import LoadingState from '@/components/exercices/LoadingState.vue'
import { useToast } from '../../composables/useToast'
const { success, error, warning, info, toast, clear } = useToast()

const authStore = useAuthStore()
const successMessage = ref('')
const showPasswordForm = ref(false)
const isLoading = ref(true)

// Formulaire principal
const form = reactive({
  username: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
})

// Formulaire de changement de mot de passe
const passwordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

// Initialiser le formulaire avec les données utilisateur
const initializeForm = () => {
  if (authStore.user) {
    form.username = authStore.user.username || ''
    form.first_name = authStore.user.first_name || ''
    form.last_name = authStore.user.last_name || ''
    form.email = authStore.user.email || ''
    form.phone = authStore.user.phone || ''
  }
}

// Réinitialiser le formulaire
const resetForm = () => {
  initializeForm()
  showPasswordForm.value = false
  passwordForm.oldPassword = ''
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
  successMessage.value = ''
  authStore.error = null
}

// Valider le formulaire de mot de passe
const validatePasswordForm = () => {
  if (!passwordForm.oldPassword || !passwordForm.newPassword) {
    return 'Tous les champs de mot de passe sont requis'
  }
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    return 'Les nouveaux mots de passe ne correspondent pas'
  }
  if (passwordForm.newPassword.length < 8) {
    return 'Le nouveau mot de passe doit contenir au moins 8 caractères'
  }
  return null
}

// Valider le numéro de téléphone
const validatePhone = () => {
  if (form.phone && !/^[\d\s+()-]{8,}$/.test(form.phone)) {
    return "Le numéro de téléphone n'est pas valide"
  }
  return null
}

// Soumettre le formulaire
const handleSubmit = async () => {
  successMessage.value = ''
  authStore.error = null

  try {
    // Valider le téléphone
    const phoneError = validatePhone()
    if (phoneError) {
      error(phoneError)
      return
    }

    let data = {
      username: form.username,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
    }

    if (passwordForm.newPassword !== '') {
      const passwordError = validatePasswordForm()
      if (passwordError) {
        error(passwordError)
        return
      }
      data['password'] = passwordForm.newPassword
    }

    // Mettre à jour le profil
    const profileResult = await authStore.updateProfile(data)

    if (!profileResult.success) {
      return
    }

    successMessage.value = 'Profil mis à jour avec succès !'
    success(successMessage.value)

    // Réinitialiser le formulaire de mot de passe si nécessaire
    if (showPasswordForm.value && passwordForm.newPassword) {
      showPasswordForm.value = false
      passwordForm.oldPassword = ''
      passwordForm.newPassword = ''
      passwordForm.confirmPassword = ''
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour du profil:', error)
  }
}

// Formater la date
const formatDate = (dateString, time = true) => {
  if (!dateString) return 'Jamais'

  const date = new Date(dateString)
  return time
    ? new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    : new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date)
}

// Surveiller les changements dans les données utilisateur
watch(
  () => authStore.user,
  () => {
    initializeForm()
  },
  { deep: true, immediate: true },
)

// Initialiser au montage
onMounted(async () => {
  initializeForm()
  await authStore.fetchUser()
  isLoading.value = false
})
</script>

<style scoped>
/* Styles personnalisés si nécessaire */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
